import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { HiOutlineBell } from 'react-icons/hi';
import { 
  fetchUserNotifications,
  fetchNotificationCount,
  markNotificationAsRead,
  selectUserNotifications
} from '../../store/slices/notificationSlice';
import './NotificationBell.scss';

const NotificationBell = () => {
  const dispatch = useDispatch();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const dropdownRef = useRef(null);
  
  const { user } = useSelector(state => state.auth);
  const { unreadCount, loading } = useSelector(state => state.notifications);
  // Use selector to get notifications filtered by user email
  const notifications = useSelector(state => 
    selectUserNotifications(state, user?.email)
  );
  
  // Fetch notification count when component mounts
  useEffect(() => {
    if (user?.email) {
      dispatch(fetchNotificationCount(user.email));
    }
  }, [dispatch, user?.email]);
  
  // Fetch notifications when dropdown is opened
  useEffect(() => {
    if (isDropdownOpen && user?.email) {
      dispatch(fetchUserNotifications({ email: user.email }));
    }
  }, [isDropdownOpen, dispatch, user?.email]);
  
  // Show email sent tooltip if a new notification has been received with emailSent=true
  useEffect(() => {
    if (notifications.length > 0) {
      const recentNotification = notifications[0];
      if (recentNotification.emailSent) {
        setEmailSent(true);
        const timer = setTimeout(() => {
          setEmailSent(false);
        }, 3000);
        return () => clearTimeout(timer);
      }
    }
  }, [notifications]);
  
  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      dispatch(markNotificationAsRead({ 
        notificationId: notification.id,
        userEmail: user.email
      }));
    }
    
    // Navigate to the action link if it exists
    if (notification.actionLink) {
      window.location.href = notification.actionLink;
    }
    
    setIsDropdownOpen(false);
  };
  
  const handleMarkAllAsRead = () => {
    if (user?.email) {
      dispatch(fetchUserNotifications({ 
        email: user.email,
        markAsRead: true 
      }));
    }
  };
  
  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  };
  
  return (
    <div className="notification-bell-container" ref={dropdownRef}>
      <button 
        className="notification-bell-button" 
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        aria-label="Notifications"
      >
        <HiOutlineBell size="28px" className="notification-icon" />
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount}</span>
        )}
      </button>
      
      {emailSent && (
        <div className="email-sent-tooltip">
          Email notification sent!
        </div>
      )}
      
      {isDropdownOpen && (
        <div className="notifications-dropdown">
          <div className="notifications-header">
            <h3>Notifications</h3>
            {unreadCount > 0 && (
              <button 
                className="mark-all-read"
                onClick={handleMarkAllAsRead}
              >
                Mark all as read
              </button>
            )}
          </div>
          
          <div className="notifications-content">
            {loading ? (
              <div className="loading-indicator">Loading notifications...</div>
            ) : notifications.length === 0 ? (
              <div className="no-notifications">
                You don't have any notifications.
              </div>
            ) : (
              <ul className="notifications-list">
                {notifications.map(notification => (
                  <li 
                    key={notification.id} 
                    className={`notification-item ${!notification.read ? 'unread' : ''}`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="notification-content">
                      <div className="notification-title">{notification.title}</div>
                      <div className="notification-message">{notification.message}</div>
                      <div className="notification-time">
                        {formatTime(notification.createdAt)}
                      </div>
                    </div>
                    {!notification.read && (
                      <div className="unread-indicator"></div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
          
          <div className="notifications-footer">
            <a href="/notifications" className="view-all-link">
              View all notifications
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell; 